import type {
  BacklogIssueDetail,
  BacklogProjectMetaResponse,
  BacklogAuthorization,
  BacklogConfigResponse,
  BacklogConnectionResponse,
  BacklogMembersResponse,
  BacklogTaskOverride,
  CreateBacklogTasksResponse,
  UpdateBacklogIssuePayload,
  UpdateBacklogIssueResponse,
} from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

/** A batch walks Backlog one issue at a time; ten items take five to eight seconds. */
const SYNC_TIMEOUT_MS = 120_000

/**
 * Whether Backlog is wired up at all.
 *
 * Resolves to null instead of throwing: the sync panel is an extra on a screen
 * that works without it, so a space nobody has configured should leave the
 * minutes exactly as they were rather than showing an error the reader cannot act on.
 */
export async function fetchBacklogConfig(): Promise<BacklogConfigResponse | null> {
  try {
    const { data } = await apiClient.get<BacklogConfigResponse>('/api/backlog/config')
    return data.configured ? data : null
  } catch {
    return null
  }
}

/**
 * Project members for the assignee picker.
 *
 * @param projectKey Which project's roster to return. Omitted leaves upstream
 *   on the space default — same contract as {@link fetchBacklogProjectMeta}.
 * @throws ApiError when the roster cannot be read (no grant, upstream down, …).
 *   Callers that only decorate a picker surface the message and offer retry;
 *   Sync still runs without a roster and falls back to the name in the minutes.
 */
export async function fetchBacklogMembers(
  projectKey?: string | null,
): Promise<BacklogMembersResponse> {
  try {
    const { data } = await apiClient.get<BacklogMembersResponse>('/api/backlog/members', {
      params: projectKey ? { project_key: projectKey } : undefined,
    })
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().membersLoadError)
  }
}

/**
 * Send the meeting's saved action items to Backlog, or preview what sending them would do.
 *
 * Unlike the two reads above this one throws, because the reviewer pressed a
 * button and is owed an answer either way.
 *
 * @param dryRun true builds the payloads and reports each verdict without writing.
 * @param overrides The account the reviewer picked, per row index.
 * @throws ApiError carrying the server's own sentence, which names the ticket
 *   or the limit that was refused.
 */
export async function syncBacklogTasks(
  meetingId: string,
  { dryRun = false, overrides = [] }: { dryRun?: boolean; overrides?: BacklogTaskOverride[] } = {}
): Promise<CreateBacklogTasksResponse> {
  try {
    const { data } = await apiClient.post<CreateBacklogTasksResponse>(
      `/api/meetings/${meetingId}/backlog/tasks`,
      { dry_run: dryRun, overrides },
      { timeout: SYNC_TIMEOUT_MS }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSyncBacklog)
  }
}

/**
 * One matched issue as Backlog has it this second.
 *
 * Throws rather than resolving to null: it backs the panel that asks before
 * overwriting, and a panel that cannot say what it is about to replace must
 * not open at all.
 *
 * @throws ApiError carrying the server's own sentence.
 */
export async function fetchBacklogIssue(
  meetingId: string,
  issueKey: string,
  against: { dueDate?: string; assigneeId?: number | null; confirmedUpdated?: string | null } = {}
): Promise<BacklogIssueDetail> {
  try {
    // Sent so the answer carries `conflicts[]` computed against what the write
    // would set. Asked bare it reports the issue and nothing about whether
    // overwriting it is safe.
    const { data } = await apiClient.get<BacklogIssueDetail>(
      `/api/meetings/${meetingId}/backlog/issues/${encodeURIComponent(issueKey)}`,
      {
        params: {
          ...(against.dueDate ? { due_date: against.dueDate } : {}),
          ...(against.assigneeId != null ? { assignee_id: against.assigneeId } : {}),
          ...(against.confirmedUpdated ? { confirmed_updated: against.confirmedUpdated } : {}),
        },
      }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSyncBacklog)
  }
}

/**
 * The priority and issue-type names this project accepts.
 *
 * Resolves to null rather than throwing, like the other two reads that only
 * furnish a picker: a project whose names cannot be read still leaves the
 * panel usable for everything except changing priority.
 *
 * @param projectKey The meeting's own Backlog project, so the names offered are
 *   the ones that project defines. Omitted for a meeting attached to no
 *   project, which leaves upstream on the space's configured one. Passing it
 *   matters because these names are validated per project on the way back in: a
 *   type one project declares is refused by another with 422.
 */
export async function fetchBacklogProjectMeta(
  projectKey?: string | null
): Promise<BacklogProjectMetaResponse | null> {
  try {
    const { data } = await apiClient.get<BacklogProjectMetaResponse>(
      '/api/backlog/project-meta',
      projectKey ? { params: { project_key: projectKey } } : undefined
    )
    return data
  } catch {
    return null
  }
}

/**
 * Apply the overwrite the reviewer confirmed.
 *
 * @param payload Only the fields they approved; absent leaves one alone.
 * @throws ApiError carrying the server's own sentence — which, when the issue
 *   has moved on since the panel read it, is the refusal that stops a silent
 *   overwrite.
 */
export async function updateBacklogIssue(
  meetingId: string,
  issueKey: string,
  payload: UpdateBacklogIssuePayload
): Promise<UpdateBacklogIssueResponse> {
  try {
    const { data } = await apiClient.patch<UpdateBacklogIssueResponse>(
      `/api/meetings/${meetingId}/backlog/issues/${encodeURIComponent(issueKey)}`,
      payload,
      { timeout: SYNC_TIMEOUT_MS }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSyncBacklog)
  }
}

/**
 * Whether the reader's own Backlog key still works.
 *
 * Throws rather than resolving to null like the two reads above: this one
 * backs a panel whose entire purpose is to report that answer, so swallowing
 * the failure would leave it saying nothing at all.
 */
export async function fetchBacklogConnection(): Promise<BacklogConnectionResponse> {
  try {
    const { data } = await apiClient.get<BacklogConnectionResponse>('/api/backlog/connection')
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedBacklogConnection)
  }
}

/**
 * Give up this account's stored grant, so the application stops acting as them.
 *
 * Ends this application's own copy only. The approval on the Backlog side
 * belongs to its owner and is revoked there; the screen says as much rather
 * than claiming more than this call does.
 *
 * @throws ApiError when the server refuses; 204 and "there was nothing stored"
 *   both come back as success, because both leave the caller with no grant.
 */
export async function disconnectBacklog(): Promise<void> {
  try {
    await apiClient.delete('/api/backlog/connection')
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedBacklogConnection)
  }
}

/**
 * Ask the server where to send the browser to approve this application.
 *
 * POST although it reads like a fetch: the server mints a signed, single-purpose state for this
 * user, and a URL a browser may prefetch is the wrong place for that.
 *
 * @throws ApiError when this deployment has no Backlog client credentials, or no space.
 */
export async function startBacklogOauth(): Promise<BacklogAuthorization> {
  try {
    const { data } = await apiClient.post<BacklogAuthorization>('/api/backlog/connection/oauth')
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedBacklogConnect)
  }
}

/**
 * Hand back what Backlog put on the redirect, and get the finished connection.
 *
 * Answers with the connection rather than with nothing, so the screen can show the result without
 * a second round trip.
 */
export async function completeBacklogOauth(
  code: string,
  state: string,
): Promise<BacklogConnectionResponse> {
  try {
    const { data } = await apiClient.post<BacklogConnectionResponse>(
      '/api/backlog/connection/oauth/complete',
      { code, state },
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedBacklogConnect)
  }
}
